import { memo } from 'react';

import { getGroupIdStringFromId } from '@infinex/asset-config';

import { ChainAssetBalanceListItem } from './ChainAssetBalanceListItem';
import type { OnAssetSelection } from './type';

import type { ChainAssetBalanceSummed } from '../../features/balances';
import { calculateAssetDecimalPrecision } from '../../utils/index';
import { AssetIcon } from '../crates/AssetIcon';
import { FormattedNumber } from '../formatted-number';

export interface ChainTokenBalanceListItemProps {
  chainAssetBalance: ChainAssetBalanceSummed;
  /** If true, applies accent highlight to selected items */
  highlightSelected?: boolean;
  /** Whether this item is the currently selected asset */
  isSelected?: boolean;
  /** Whether this item is disabled */
  isDisabled?: boolean;
  disabledLabel?: string;
  disabledDetails?: string;
  buildTestId: (...args: string[]) => string | undefined;
  /** Selection mode: single (click to select) or multi (checkbox toggle) */
  selectionMode?: 'single' | 'multi';
  /** Callback for single-select mode */
  onSelection: OnAssetSelection;
  /** Callback for multi-select toggle */
  onToggle?: () => void;
  showWalletSources?: boolean;
  className?: string;
}

export const ChainTokenBalanceListItem = memo(
  ({
    chainAssetBalance,
    onSelection,
    onToggle,
    selectionMode = 'single',
    highlightSelected = false,
    isSelected = false,
    buildTestId,
    isDisabled,
    disabledLabel,
    disabledDetails,
    showWalletSources = true,
    className,
  }: ChainTokenBalanceListItemProps) => {
    const testIdRoot = 'token-list-item';

    const groupId = chainAssetBalance.groupId;

    const assetAddress = chainAssetBalance.assetAddress.toString();
    const assetDecimals = chainAssetBalance.decimals;

    const totalBalanceUsd = chainAssetBalance.balanceUsd;

    const assetDecimalPrecision =
      chainAssetBalance && chainAssetBalance.rateUsd
        ? calculateAssetDecimalPrecision({
            tokenAmount: chainAssetBalance.decimalBalance.toString(),
            tokenDecimals: assetDecimals ?? 0,
            rateUsd: chainAssetBalance.rateUsd,
          })
        : undefined;

    const isUnofficial = !chainAssetBalance.isOfficial;

    return (
      <ChainAssetBalanceListItem
        className={className}
        assetIcon={
          <AssetIcon
            {...groupId}
            chainAdornment={chainAssetBalance.chain}
            size="large"
          />
        }
        primaryTitle={chainAssetBalance.name}
        secondaryTitle={
          <span>
            {chainAssetBalance.decimalBalance.gt(0) ? (
              <>
                <FormattedNumber
                  value={chainAssetBalance.decimalBalance.toNumber()}
                  decimals={assetDecimalPrecision}
                />{' '}
              </>
            ) : null}
            {chainAssetBalance.symbol}
          </span>
        }
        usdValue={totalBalanceUsd}
        sources={chainAssetBalance.sources}
        showWalletSources={showWalletSources}
        isUnofficial={isUnofficial}
        highlightSelected={highlightSelected}
        isSelected={isSelected}
        isDisabled={isDisabled}
        disabledLabel={disabledLabel}
        disabledDetails={disabledDetails}
        testId={buildTestId(
          testIdRoot,
          getGroupIdStringFromId(groupId),
          chainAssetBalance.chain
        )}
        selectionMode={selectionMode}
        onSelection={() => {
          onSelection({
            // it can't be a position
            assetType:
              chainAssetBalance.type === 'position'
                ? 'token'
                : chainAssetBalance.type,
            assetGroupId: chainAssetBalance.assetId,
            chain: chainAssetBalance.chain,
            address: assetAddress,
            decimals: assetDecimals,
            walletIds: chainAssetBalance.walletIds || [],
            // Pass all sources (including disabled) so the source selection list
            // can show disabled items greyed out rather than hiding them.
            sources: chainAssetBalance.sources,
          });
        }}
        onToggle={onToggle}
      />
    );
  }
);

ChainTokenBalanceListItem.displayName = 'ChainTokenBalanceListItem';
